import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { Spinner, SectionHeader } from "@/components/common";
import { PersonRailCard } from "@/components/PersonCard";
import { ListingCard } from "@/components/ListingCard";
import { TribeCard } from "@/components/TribeCard";
import { CompatibilityRing } from "@/components/CompatibilityRing";
import { resolveImg } from "@/lib/api";
import { Check, MapPin, ArrowRight, Sparkles } from "lucide-react";

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [savedIds, setSavedIds] = useState([]);

  useEffect(() => {
    api.get("/home").then((r) => setData(r.data)).catch(() => {});
    api.get("/saved/ids").then((r) => setSavedIds(r.data.ids)).catch(() => {});
  }, []);

  const onSave = (id, on) => setSavedIds((s) => (on ? [...s, id] : s.filter((x) => x !== id)));

  if (!data) return <div><TopBar greeting={`Hi ${user?.name?.split(" ")[0] || ""} 👋`} name="Your home feed" /><Spinner /></div>;

  const top = data.best_matches?.[0];

  return (
    <div>
      <TopBar greeting={`Hi ${user?.name?.split(" ")[0] || ""} 👋`} name="Your home feed" />
      <div className="space-y-8 px-4 pt-5">
        {top && (
          <section className="ft-rise">
            <SectionHeader title="Your top match today" sub="Based on your shared preferences" />
            <div
              data-testid="home-top-match"
              onClick={() => navigate(`/person/${top.user_id}`)}
              className="cursor-pointer overflow-hidden rounded-3xl bg-ink text-white shadow-card"
            >
              <div className="flex gap-4 p-5">
                <img src={resolveImg(top.profile?.photo_url)} alt="" className="h-24 w-24 rounded-2xl object-cover" />
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-heading text-2xl font-extrabold leading-none">{top.profile?.first_name}</h3>
                      <p className="mt-1 text-sm text-white/70">{top.profile?.occupation}</p>
                    </div>
                    <div className="rounded-2xl bg-white/95 p-1 shadow-soft"><CompatibilityRing score={top.compatibility?.score} size={52} stroke={5} /></div>
                  </div>
                </div>
              </div>
              <div className="space-y-1.5 bg-white/5 px-5 py-4">
                {(top.compatibility?.matches || []).slice(0, 3).map((m) => (
                  <p key={m} className="flex items-center gap-2 text-sm text-white/90"><Check className="h-4 w-4 text-mint" /> {m}</p>
                ))}
                <div className="flex items-center gap-1 pt-1 text-sm font-bold text-brand">View profile <ArrowRight className="h-4 w-4" /></div>
              </div>
            </div>
          </section>
        )}

        {data.best_matches?.length > 1 && (
          <section>
            <SectionHeader title="Your best matches" action={<button data-testid="see-all-matches" onClick={() => navigate("/matches")} className="text-sm font-bold text-brand">See all</button>} />
            <div className="no-scrollbar -mx-4 flex gap-3 overflow-x-auto px-4 pb-2">
              {data.best_matches.map((p) => <PersonRailCard key={p.user_id} person={p} />)}
            </div>
          </section>
        )}

        <section>
          <SectionHeader title="Rooms for you" sub="Matched to your budget & vibe" action={<button onClick={() => navigate("/discover")} className="text-sm font-bold text-brand">See all</button>} />
          <div className="space-y-5">
            {(data.rooms_for_you || []).slice(0, 3).map((l) => <ListingCard key={l.listing_id} listing={l} savedIds={savedIds} onSaveChange={onSave} />)}
          </div>
        </section>

        {data.people_in_area?.length > 0 && (
          <section className="rounded-3xl bg-gradient-to-br from-brand to-brand-dark p-5 text-white">
            <div className="flex items-center gap-2"><Sparkles className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-widest">In your area</span></div>
            <h3 className="mt-2 font-heading text-2xl font-extrabold">{data.people_in_area.length} people are looking around {data.area_name || "your city"}</h3>
            <p className="mt-1 text-sm text-white/80">Team up and rent a place together.</p>
            <div className="mt-4 flex -space-x-3">
              {data.people_in_area.slice(0, 6).map((p) => (
                <img key={p.user_id} src={resolveImg(p.profile?.photo_url)} alt="" className="h-11 w-11 rounded-full border-2 border-white object-cover" />
              ))}
            </div>
            <button data-testid="explore-people-area" onClick={() => navigate("/discover?tab=people")} className="mt-4 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-brand">Discover people</button>
          </section>
        )}

        {data.tribes?.length > 0 && (
          <section>
            <SectionHeader title="Tribes forming near you" action={<button onClick={() => navigate("/discover?tab=tribes")} className="text-sm font-bold text-brand">See all</button>} />
            <div className="no-scrollbar -mx-4 flex gap-4 overflow-x-auto px-4 pb-2">
              {data.tribes.map((t) => <div key={t.tribe_id} className="w-[300px] shrink-0"><TribeCard tribe={t} /></div>)}
            </div>
          </section>
        )}

        <section>
          <SectionHeader title="Newly posted" sub="Fresh listings this week" />
          <div className="no-scrollbar -mx-4 flex gap-3 overflow-x-auto px-4 pb-2">
            {(data.newly_posted || []).map((l) => (
              <div key={l.listing_id} className="w-64 shrink-0"><ListingCard listing={l} savedIds={savedIds} onSaveChange={onSave} /></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
